"use client"
import { motion } from 'framer-motion';
import { Facebook, Linkedin, Instagram, Youtube } from 'lucide-react';
import { TwitterIcon, WhatsAppIcon } from "@/components/dashboard/socials-dropup";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useAppSelector } from "@/app/store/hooks";
import { useSubmitMessageMutation } from "@/app/store/api/contactApi";
import { LoginPopover } from "@/components/auth/login-popover";
import { toast } from "sonner";

const ContactUs = () => {
    const searchParams = useSearchParams();
    const { user, isAuthenticated } = useAppSelector((state) => state.auth);
    const [submitMessage, { isLoading }] = useSubmitMessageMutation();
    const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false);

    const [formData, setFormData] = useState({
        fullName: "",
        companyName: "",
        email: "",
        phone: "",
        message: "",
        category: ""
    });

    const [errors, setErrors] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const categoryParam = searchParams.get("category");
        if (categoryParam) {
            setFormData(prev => ({ ...prev, category: categoryParam }));
        }
    }, [searchParams]);

    const validateForm = () => {
        const newErrors: Record<string, boolean> = {};
        if (!formData.fullName.trim()) newErrors.fullName = true;
        if (!formData.email.trim()) newErrors.email = true;
        if (!formData.message.trim()) newErrors.message = true;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isAuthenticated) {
            setIsLoginPopoverOpen(true);
            return;
        }

        if (!validateForm()) {
            toast.error("Please fill in all required fields.");
            return;
        }

        try {
            await submitMessage({
                userId: user?._id || "",
                ...formData
            }).unwrap();
            
            toast.success("Message sent successfully! We'll get back to you soon.");
            setFormData({
                fullName: "",
                companyName: "",
                email: "",
                phone: "",
                message: "",
                category: searchParams.get("category") || ""
            });
        } catch (err: any) {
            const message = err?.data?.message || err?.message || "Failed to send message. Please try again.";
            toast.error(message);
        }
    };

    return (
        <div className="relative w-full min-h-screen pt-28 pb-16 px-4 md:px-6 bg-blue-50/50 font-jakarta overflow-hidden flex items-center justify-center">

            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-200/40 rounded-full blur-[100px] -z-10 opacity-60 translate-x-1/3 -translate-y-1/3 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-200/40 rounded-full blur-[100px] -z-10 opacity-60 -translate-x-1/3 translate-y-1/3 pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-full max-w-5xl bg-white rounded-[2rem] shadow-2xl shadow-blue-900/10 overflow-hidden flex flex-col lg:flex-row min-h-[auto]"
            >
                {/* Left Side: Info */}
                <div className="lg:w-[40%] p-8 md:p-12 flex flex-col justify-between relative bg-white">
                    {/* Content */}
                    <div>
                        <h1 className="text-4xl md:text-5xl font-syne font-bold text-slate-900 mb-4 tracking-tight">
                            Let's Talk
                        </h1>
                        <p className="text-slate-600 text-sm leading-relaxed mb-8 max-w-xs">
                            Have a question or want to discuss a project? Reach out to us using the form.
                        </p>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-slate-900 text-sm font-bold mb-1">Our Office</h3>
                                <p className="text-slate-500 text-xs leading-relaxed">
                                   2nd & 4th Floor, 1348, 7th Avenue, E End Main Rd, opposite Yes Bank, Jayanagara 9th Block, Jayanagar, Bengaluru, 560041
                                </p>
                            </div>

                            <div>
                                <h3 className="text-slate-900 text-sm font-bold mb-1">Email Us</h3>
                                <a href="mailto:support@deckwale.com" className="text-[#2563EB] text-sm font-medium hover:underline">
                                    support@deckwale.com
                                </a>
                            </div>

                            {/* Social Icons */}
                            <div className="flex gap-3 pt-2">
                                {[
                                    { Icon: Linkedin, href: "https://www.linkedin.com/company/deckwale" },
                                    { Icon: TwitterIcon, href: "https://x.com/Deckwale" },
                                    { Icon: Instagram, href: "https://www.instagram.com/deckwale/" },
                                    // { Icon: Facebook, href: "https://www.facebook.com/deckwale" },
                                    { Icon: WhatsAppIcon, href: "https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq" },
                                    { Icon: Youtube, href: "https://www.youtube.com/@DeckwaleAI" }
                                ].map((social, idx) => (
                                    <a 
                                        key={idx} 
                                        href={social.href} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:border-[#2563EB] hover:text-[#2563EB] transition-all"
                                    >
                                        {social.Icon === TwitterIcon ? (
                                            <social.Icon className="w-3.5 h-3.5" />
                                        ) : social.Icon === WhatsAppIcon ? (
                                            <social.Icon className="w-3.5 h-3.5" />
                                        ) : (
                                            <social.Icon size={14} />
                                        )}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Decorative Circle Bottom Left */}
                    <div className="absolute bottom-[-40px] left-[-40px] w-32 h-32 bg-[#2563EB]/5 rounded-full blur-xl pointer-events-none" />
                </div>

                {/* Right Side: Form */}
                <div className="lg:w-[60%] p-8 md:p-12 bg-[#1E40AF] text-white">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Full Name */}
                        <div className="group">
                            <label className={`block text-sm font-medium mb-1 transition-colors ${errors.fullName ? 'text-red-400' : 'text-blue-200 group-focus-within:text-white'}`}>Full name*</label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                required
                                className={`w-full border-b py-2 text-sm text-white font-medium focus:outline-none transition-colors bg-transparent placeholder-transparent ${errors.fullName ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-white'}`}
                                placeholder="Enter your name"
                            />
                            <p className={`text-[10px] mt-1 transition-opacity duration-300 ${errors.fullName ? 'text-red-400 opacity-100' : 'text-blue-200 opacity-0 group-focus-within:opacity-100'}`}>Please enter your full name.</p>
                        </div>

                        {/* Company Name */}
                        <div className="group">
                            <label className="block text-sm font-medium text-blue-200 mb-1 group-focus-within:text-white transition-colors">Company name</label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                className="w-full border-b border-white/20 py-2 text-sm text-white font-medium focus:outline-none focus:border-white transition-colors bg-transparent placeholder-transparent"
                                placeholder="Deckwale Inc."
                            />
                            <p className="text-[10px] text-blue-200 mt-1 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300">Your organization name.</p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Email */}
                            <div className="group">
                                <label className={`block text-sm font-medium mb-1 transition-colors ${errors.email ? 'text-red-400' : 'text-blue-200 group-focus-within:text-white'}`}>Email address*</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className={`w-full border-b py-2 text-sm text-white font-medium focus:outline-none transition-colors bg-transparent placeholder-transparent ${errors.email ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-white'}`}
                                    placeholder="john@example.com"
                                />
                                <p className={`text-[10px] mt-1 transition-opacity duration-300 ${errors.email ? 'text-red-400 opacity-100' : 'text-blue-200 opacity-0 group-focus-within:opacity-100'}`}>We’ll use this to respond.</p>
                            </div>

                            {/* Phone */}
                            <div className="group">
                                <label className="block text-sm font-medium text-blue-200 mb-1 group-focus-within:text-white transition-colors">Phone number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full border-b border-white/20 py-2 text-sm text-white font-medium focus:outline-none focus:border-white transition-colors bg-transparent placeholder-transparent"
                                    placeholder="+1 (555) 000-0000"
                                />
                                <p className="text-[10px] text-blue-200 mt-1 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300">Optional, useful for follow-up.</p>
                            </div>
                        </div>

                        {/* Message */}
                        <div className="group">
                            <label className={`block text-sm font-medium mb-1 transition-colors ${errors.message ? 'text-red-400' : 'text-blue-200 group-focus-within:text-white'}`}>Message*</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                rows={2}
                                className={`w-full border-b py-2 text-sm text-white font-medium focus:outline-none transition-colors bg-transparent placeholder-transparent resize-none ${errors.message ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-white'}`}
                                placeholder="How can we help?"
                            ></textarea>
                            <p className={`text-[10px] mt-1 transition-opacity duration-300 ${errors.message ? 'text-red-400 opacity-100' : 'text-blue-200 opacity-0 group-focus-within:opacity-100'}`}>Tell us how we can help.</p>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-white text-[#1E40AF] py-3 rounded-lg text-sm font-bold cursor-pointer hover:bg-blue-50 transition-all transform hover:scale-[1.01] shadow-lg shadow-black/10 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {isLoading ? "Sending..." : "Send Message"}
                            </button>
                        </div>

                    </form>
                </div>

            </motion.div>

            <LoginPopover 
                isOpen={isLoginPopoverOpen} 
                onClose={() => setIsLoginPopoverOpen(false)} 
            />
        </div>
    );
};

export default ContactUs;
